import { useMemo, useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import type {
  ContractStatus,
  DashboardClinicianData,
} from "@/hooks/queries/org-admin-dashboard-types";
import {
  useRemoveClinicianFromSeat,
  useResetClinicianPassword,
  useUpdateClinicianDetails,
} from "@/hooks/queries/useOrgMemberActions";
import { ORGANIZATION_ROLE_LABELS } from "@/routes/org-admin/add-person-schema";
import { ChangeEmailDialog } from "@/routes/org-admin/ChangeEmailDialog";
import { contractNotice } from "@/routes/org-admin/contract-notice";
import {
  TARGET_BLOCKED_REASON,
  TARGET_BLOCKED_TITLE,
  TARGET_NOT_ACTIVATED_REASON,
  toMemberActionError,
  type MemberActionBanner,
} from "@/routes/org-admin/member-actions-error";
import {
  accountStatePill,
  calibrationPill,
  memberSubscriptionSummary,
  SEAT_STAYS_CONSUMED_SENTENCE,
} from "@/routes/org-admin/dashboard-derivations";

// The Edit-member modal (Screen 26 :243-340). Story 14.11 shipped it read-only;
// Stories 14.4 + 14.5 made its Identity fields, its email correction and its
// password reset live, and Story 14.6 fills the last slot — seat removal
// (seam-1411-remove-seat-slot now closed). Every affordance the modal renders is
// live; nothing here is a placeholder any more.
//
// Two deliberate departures from the mockup, both recorded in the story's W3
// contract: the email helper's "sends a verification email to the new address"
// contradicts the client-confirmed 14.4 flow (immediate change, notification to
// the OLD address), so no such copy appears; and the seat Action select's
// "Assign seat from available pool" is unrepresentable — assignment happens at
// invitation, and FR83 keeps a removed clinician's seat consumed for the
// contract term, so no freed-seat affordance exists anywhere on this screen.

/** AC7 — the extra line an INVITED target's confirmation carries. */
export const REMOVE_SEAT_INVITED_COPY =
  "This person has not activated their account yet. Their invitation will stop working.";

/** 14.4 AC9 — the post-change reassurance, on the modal that made the change. */
export const EMAIL_CHANGED_NOTE =
  "A notification was sent to the previous address.";

// Screen 26 :259-266. Both were dead weight beside 14.11's disabled inputs and
// are load-bearing now that the field is live: the role label is the one Identity
// field an admin could mistake for a permission grant, and the display name is
// the one they cannot fix here.
export const DISPLAY_NAME_HINT = "Edit via the user's own profile.";
export const ROLE_LABEL_HINT =
  "Display label only. All non-clinician labels share the same organization admin permissions.";

const RESET_REASON_ID = "edit-member-reset-unavailable-reason";
/**
 * The ONE reason element for every contract-blocked affordance in this dialog
 * (E14 UI verification F3). One element, four `aria-describedby` references —
 * four copies of the same sentence would be four things to keep in step, and AT
 * would read the situation out once per control.
 */
export const CONTRACT_REASON_ID = "edit-member-contract-blocked-reason";

/**
 * The ONE reason element for the CITS block (15.8 AC7), on the same argument as
 * `CONTRACT_REASON_ID` above: four controls refuse for one reason, so there is
 * one element and four `aria-describedby` references pointing at it.
 */
export const BLOCKED_REASON_ID = "edit-member-target-blocked-reason";

/**
 * Join the reason ids that are live into one `aria-describedby`, dropping the
 * ones that are not. Several refusals can be true at once — a lapsed contract
 * on a blocked LearnWorlds row makes three — and an admin told only the first
 * would fix it and meet the next. `undefined` when none apply, because an empty
 * `aria-describedby` still names an element.
 */
function reasonIds(...ids: (string | false | null | undefined)[]) {
  return ids.filter(Boolean).join(" ") || undefined;
}

export function EditMemberDialog({
  member,
  requiredCaseCount,
  contractStatus,
  contractEndDate,
  onOpenChange,
}: {
  /** The row being inspected; `null` closes the dialog. */
  member: DashboardClinicianData | null;
  requiredCaseCount: number;
  /**
   * The host's already-loaded `contract_status` (F3). Passed down rather than
   * re-fetched: it is a top-level key on the SAME cached dashboard payload the
   * host rendered this row from, so a second read could only ever disagree with
   * the banner the admin is looking at.
   *
   * REQUIRED, and `| undefined` rather than `?:` on purpose. The value may well
   * be undefined (a payload predating 14.12 — see `contractNotice`), but the
   * PROP may not be omitted: an optional one lets a second host mount this
   * dialog with every member write live and no type error, which is the defect
   * F3 exists to close, re-created by a future caller.
   */
  contractStatus: ContractStatus | undefined;
  contractEndDate: string | null | undefined;
  onOpenChange: (open: boolean) => void;
}) {
  if (!member) return null;
  // Keyed on the row so every open starts from the server's values: the parent
  // holds the row it opened with, so without this a second open would inherit
  // the first target's draft edits.
  return (
    <EditMemberDialogBody
      key={member.user_id}
      member={member}
      requiredCaseCount={requiredCaseCount}
      contractStatus={contractStatus}
      contractEndDate={contractEndDate}
      onOpenChange={onOpenChange}
    />
  );
}

function EditMemberDialogBody({
  member,
  requiredCaseCount,
  contractStatus,
  contractEndDate,
  onOpenChange,
}: {
  member: DashboardClinicianData;
  requiredCaseCount: number;
  contractStatus: ContractStatus | undefined;
  contractEndDate: string | null | undefined;
  onOpenChange: (open: boolean) => void;
}) {
  const [roleLabel, setRoleLabel] = useState(member.organization_role_label);
  const [department, setDepartment] = useState(member.department ?? "");
  const [hrId, setHrId] = useState(member.hr_id ?? "");
  // What the server currently holds. Starts as the row the modal opened with and
  // advances to what a save actually persisted — the parent still holds its
  // pre-save snapshot, so comparing against `member` would leave Save armed on
  // an edit that already landed.
  const [persisted, setPersisted] = useState({
    organization_role_label: member.organization_role_label,
    department: member.department,
    hr_id: member.hr_id,
  });
  const [detailsBanner, setDetailsBanner] = useState<MemberActionBanner | null>(
    null,
  );
  const [detailsSaved, setDetailsSaved] = useState(false);

  const [changingEmail, setChangingEmail] = useState(false);
  // The dashboard refetches on success, but the row this modal was opened with
  // is a snapshot the parent still holds — so the confirmed address is tracked
  // here to keep the open modal honest about what it just did.
  const [email, setEmail] = useState(member.email);
  const [emailChanged, setEmailChanged] = useState(false);

  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetBanner, setResetBanner] = useState<MemberActionBanner | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [removeBanner, setRemoveBanner] = useState<MemberActionBanner | null>(
    null,
  );

  const updateDetails = useUpdateClinicianDetails(member.user_id);
  const resetPassword = useResetClinicianPassword(member.user_id);
  const removeSeat = useRemoveClinicianFromSeat(member.user_id);

  const name =
    [member.first_name, member.last_name].filter(Boolean).join(" ") || email;
  const calibration = calibrationPill(
    member.calibration_state,
    member.completed_cases,
    requiredCaseCount,
  );
  const seatState = accountStatePill(member.account_state);

  // ORG-011's client half — the SERVER's rule, mirrored: `user_origin ===
  // 'learnworlds_new' AND NOT is_active` (reset_clinician_password/command.py).
  // It used to read `account_state === 'invited'`, which is a DIFFERENT rule:
  // every inactive account reads as "invited" on the roster, so a blocked LEGACY
  // clinician was told they "haven't activated their account yet" — false, and
  // for an action the server would in fact have performed (DW-26).
  //
  // `account_state` is the payload's statement of `is_active`, so the mirror
  // keys on it rather than on any one literal: `active` is the only state the
  // derivation reaches while the projection is active, and every other value —
  // present or future — is one the backend deliberately distinguished from it.
  // A MISSING key is the pre-14.11 deploy-skew case (Rule 06): unknown is_active,
  // so no pre-gate, and the server answers with the same sentence if it refuses.
  // `removed` never arrives here at all — `isRowActionable` renders no Edit
  // affordance for those rows.
  //
  // Change-email deliberately stays live for the same row — correcting a typo
  // before activation is the primary reason that action exists.
  const notActivated =
    member.user_origin === "learnworlds_new" &&
    (member.account_state ?? "active") !== "active";

  // 15.8 AC9 — the CITS block's client half, and its OWN predicate. It keys on
  // `account_state === 'blocked'` and on nothing else, because the server's rule
  // is `blocked_at IS NOT NULL` and `account_state` is the payload's only
  // statement of it.
  //
  // Deliberately NOT folded into `notActivated` above. The two rules genuinely
  // differ — a block applies to any origin, while ORG-011 needs
  // `learnworlds_new` AND inactive — and one flag serving two rules is exactly
  // the defect DW-26 filed against the line above. A blocked LEGACY clinician
  // must be told about CITS and never that they "haven't activated".
  //
  // Fail-SOFT on deploy skew, mirroring `notActivated`: an absent
  // `account_state`, or a value this bundle does not recognise, gets NO
  // pre-gate. The pill's own fallback (`UNKNOWN_ACCOUNT_STATE_PILL`) fails
  // closed, but a LABEL claims nothing and must not drive a refusal — the
  // server is the enforcement, and if it refuses, the ORG-017 banner carries
  // this very sentence.
  const blocked = member.account_state === "blocked";

  // 14.6 AC7's extra removal line is a DIFFERENT rule and now has its own name.
  // It is about the INVITATION — "their invitation will stop working" — so it
  // keys on the roster state, not on ORG-011. Sharing one flag between the two
  // is what produced DW-26 one story earlier; two rules, two predicates.
  const isInvited = member.account_state === "invited";

  // F3 — the contract window, mirrored from the SERVER's rule: every member
  // WRITE on this dialog is refused 403 ORG-010 outside it (change-email,
  // remove-seat, update-details, reset-password), while reads stay open. The
  // screen behind already gates "Add person" on exactly this; the dialog ran no
  // policy at all, so a reset walked the admin through the destructive
  // confirmation and produced the refusal only afterwards.
  //
  // `contractNotice` — not a hand-rolled `!== "active"` — is what makes the
  // reason here and the banner on the host the SAME WORDS by construction, and
  // it is deliberately null for `undefined` too: a dialog must not gate on a
  // state it has not read (the server is the enforcement). The host only mounts
  // this dialog once `data` has loaded, so that case is the deploy-skew one.
  const contract = contractNotice(contractStatus, contractEndDate);
  const contractBlocked = contract !== null;

  // Blank optionals normalize to null, matching the backend's blank → None
  // coercion, so "cleared the field" and "never had one" are one draft state.
  const draft = {
    organization_role_label: roleLabel.trim(),
    department: department.trim() || null,
    hr_id: hrId.trim() || null,
  };
  const dirty =
    draft.organization_role_label !== persisted.organization_role_label ||
    draft.department !== (persisted.department ?? null) ||
    draft.hr_id !== (persisted.hr_id ?? null);
  const canSave =
    dirty &&
    draft.organization_role_label.length > 0 &&
    !contractBlocked &&
    !blocked &&
    !updateDetails.isPending;

  function onSave() {
    setDetailsBanner(null);
    setDetailsSaved(false);
    updateDetails.mutate(draft, {
      onSuccess: (data) => {
        // Render what actually persisted, not what was typed.
        setRoleLabel(data.organization_role_label);
        setDepartment(data.department ?? "");
        setHrId(data.hr_id ?? "");
        setPersisted({
          organization_role_label: data.organization_role_label,
          department: data.department,
          hr_id: data.hr_id,
        });
        setDetailsSaved(true);
      },
      onError: (error) => setDetailsBanner(toMemberActionError(error)),
    });
  }

  function onRemovalConfirmed() {
    setRemoveBanner(null);
    removeSeat.mutate(undefined, {
      // Both dialogs close on success: the member this modal edits no longer
      // holds a seat, so leaving their Identity fields open and saveable would
      // offer edits the API now refuses ORG-008.
      onSuccess: () => {
        setConfirmingRemoval(false);
        onOpenChange(false);
      },
      onError: (error) => {
        setConfirmingRemoval(false);
        setRemoveBanner(toMemberActionError(error));
      },
    });
  }

  function onResetConfirmed() {
    setResetBanner(null);
    setResetSentTo(null);
    resetPassword.mutate(undefined, {
      onSuccess: (data) => {
        setConfirmingReset(false);
        setResetSentTo(data.emailed_to);
      },
      onError: (error) => {
        setConfirmingReset(false);
        setResetBanner(toMemberActionError(error));
      },
    });
  }

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{`Edit · ${name}`}</DialogTitle>
            <DialogDescription>
              Organization-scoped details. Capacity, contract dates, and
              legacy-certificate verification stay with the CITS admin team.
            </DialogDescription>
          </DialogHeader>

          {/* F3 — the same <Banner> with the same `contractNotice` output the
              host renders behind this modal. Composed from the shared copy
              rather than written out, so the dialog and the banner cannot come
              to describe one contract state in two different ways. */}
          {contract ? (
            <Banner id={CONTRACT_REASON_ID} variant="warn" title={contract.title}>
              {contract.body}
            </Banner>
          ) : null}

          {/* 15.8 AC7/AC8 — the CITS block, stated once for all four controls.
              The body is the SAME exported constant `toMemberActionError` hands
              back for a 409 ORG-017, so an admin who defeats this pre-gate and
              reaches the server gets this sentence again rather than a second,
              differently-worded account of one situation. `info`, not `warn`:
              nothing went wrong here and nothing needs re-checking. */}
          {blocked ? (
            <Banner
              id={BLOCKED_REASON_ID}
              variant="info"
              title={TARGET_BLOCKED_TITLE}
            >
              {TARGET_BLOCKED_REASON}
            </Banner>
          ) : null}

          <Section title="Identity">
            <ReadOnlyField
              label="Display name"
              value={name}
              hint={DISPLAY_NAME_HINT}
            />
            <RoleLabelField value={roleLabel} onChange={setRoleLabel} />
            <EditableField
              label="Department"
              value={department}
              onChange={setDepartment}
            />
            <EditableField label="HR ID" value={hrId} onChange={setHrId} />
            <div className="flex flex-col gap-2 sm:col-span-2">
              <ReadOnlyField label="Work email" value={email} />
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={contractBlocked || blocked}
                  aria-disabled={contractBlocked || blocked ? "true" : undefined}
                  aria-describedby={reasonIds(
                    contractBlocked && CONTRACT_REASON_ID,
                    blocked && BLOCKED_REASON_ID,
                  )}
                  title={
                    contract?.title ?? (blocked ? TARGET_BLOCKED_REASON : undefined)
                  }
                  onClick={() => setChangingEmail(true)}
                >
                  Change email
                </Button>
              </div>
              {emailChanged ? (
                <Banner variant="success">{EMAIL_CHANGED_NOTE}</Banner>
              ) : null}
            </div>
            {detailsBanner ? (
              <div className="sm:col-span-2">
                <Banner variant={detailsBanner.variant} title={detailsBanner.title}>
                  {detailsBanner.body}
                </Banner>
              </div>
            ) : null}
            {detailsSaved ? (
              <div className="sm:col-span-2">
                <Banner variant="success">Changes saved.</Banner>
              </div>
            ) : null}
          </Section>

          <Section title="Seat">
            <ReadOnlyField label="Current state" value={seatState.label} />
            <div className="flex flex-col gap-2">
              <div className="flex items-end">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={contractBlocked || blocked || removeSeat.isPending}
                  aria-disabled={contractBlocked || blocked ? "true" : undefined}
                  aria-describedby={reasonIds(
                    contractBlocked && CONTRACT_REASON_ID,
                    blocked && BLOCKED_REASON_ID,
                  )}
                  title={
                    contract?.title ?? (blocked ? TARGET_BLOCKED_REASON : undefined)
                  }
                  onClick={() => setConfirmingRemoval(true)}
                >
                  Remove from seat
                </Button>
              </div>
              {removeBanner ? (
                <Banner variant={removeBanner.variant} title={removeBanner.title}>
                  {removeBanner.body}
                </Banner>
              ) : null}
            </div>
          </Section>

          <Section title="Calibration & subscription — read-only">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-[color:var(--text-secondary)]">
                Calibration status
              </span>
              <span className="[font-variant-numeric:tabular-nums]">
                <Badge variant={calibration.variant}>{calibration.label}</Badge>
              </span>
            </div>
            <ReadOnlyField
              label="Subscription"
              value={memberSubscriptionSummary(member)}
            />
            <p className="text-xs text-[color:var(--text-tertiary)] sm:col-span-2">
              Calibration scores and client data are never visible to organization
              admins — only state flags and dates.
            </p>
          </Section>

          <Section title="Security">
            <div className="flex flex-col gap-2 sm:col-span-2">
              <div>
                {/* Independent refusals can be true at once, so every live id
                    goes on `aria-describedby` rather than one winning: an admin
                    told only "contract ended" would fix the contract and meet
                    the next refusal after it. The order is broadest first —
                    the contract governs the whole org, the CITS block the whole
                    account, ORG-011 only this one action. */}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={
                    contractBlocked ||
                    blocked ||
                    notActivated ||
                    resetPassword.isPending
                  }
                  aria-disabled={
                    contractBlocked || blocked || notActivated ? "true" : undefined
                  }
                  aria-describedby={reasonIds(
                    contractBlocked && CONTRACT_REASON_ID,
                    blocked && BLOCKED_REASON_ID,
                    notActivated && RESET_REASON_ID,
                  )}
                  title={
                    contract?.title ??
                    (blocked ? TARGET_BLOCKED_REASON : undefined) ??
                    (notActivated ? TARGET_NOT_ACTIVATED_REASON : undefined)
                  }
                  onClick={() => setConfirmingReset(true)}
                >
                  Send password reset
                </Button>
              </div>
              {notActivated ? (
                <p
                  id={RESET_REASON_ID}
                  className="text-xs text-[color:var(--text-tertiary)]"
                >
                  {TARGET_NOT_ACTIVATED_REASON}
                </p>
              ) : null}
              {resetSentTo ? (
                <Banner variant="success">
                  {`A temporary password was emailed to ${resetSentTo}.`}
                </Banner>
              ) : null}
              {resetBanner ? (
                <Banner variant={resetBanner.variant} title={resetBanner.title}>
                  {resetBanner.body}
                </Banner>
              ) : null}
            </div>
          </Section>

          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {/* The metadata inputs stay editable on a blocked contract — Save
                alone is the refusal, which keeps the diff small and lets an
                admin draft the correction they will submit once CITS renews.
                A CITS-blocked ACCOUNT is left the same way for consistency, but
                the draft has nowhere to go: only CITS can lift that one. */}
            <Button
              onClick={onSave}
              disabled={!canSave}
              aria-disabled={contractBlocked || blocked ? "true" : undefined}
              aria-describedby={reasonIds(
                contractBlocked && CONTRACT_REASON_ID,
                blocked && BLOCKED_REASON_ID,
              )}
              title={
                contract?.title ?? (blocked ? TARGET_BLOCKED_REASON : undefined)
              }
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {changingEmail ? (
        <ChangeEmailDialog
          userId={member.user_id}
          currentEmail={email}
          userOrigin={member.user_origin}
          onCancel={() => setChangingEmail(false)}
          onChanged={(newEmail) => {
            setEmail(newEmail);
            setEmailChanged(true);
            setChangingEmail(false);
          }}
        />
      ) : null}

      {confirmingReset ? (
        <Dialog open onOpenChange={() => setConfirmingReset(false)}>
          <DialogContent className="w-[min(var(--width-md,520px),100%)]">
            <DialogHeader>
              <DialogTitle>Send password reset</DialogTitle>
              <DialogDescription>
                {`This invalidates ${name}'s current password and emails a temporary one to ${email}. They'll be asked to set a new password when they sign in.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setConfirmingReset(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={onResetConfirmed}
                disabled={resetPassword.isPending}
              >
                Send reset email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {confirmingRemoval ? (
        <Dialog open onOpenChange={() => setConfirmingRemoval(false)}>
          <DialogContent className="w-[min(var(--width-md,520px),100%)]">
            <DialogHeader>
              <DialogTitle>Remove from seat</DialogTitle>
              <DialogDescription>
                {`${name} will lose access to AMPS immediately, and only a CITS admin can restore it.`}
              </DialogDescription>
            </DialogHeader>
            {/* The FR83 sentence is a Banner, not description prose: it is the
                part an admin most needs to have read, and burying it in a
                paragraph is how "I thought removing freed the seat" happens.
                Same const as the capacity banner behind this dialog — one
                sentence, so the screen and the confirmation cannot disagree. */}
            <Banner variant="warn">{SEAT_STAYS_CONSUMED_SENTENCE}</Banner>
            {isInvited ? (
              <p className="text-sm text-[color:var(--text-secondary)]">
                {REMOVE_SEAT_INVITED_COPY}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setConfirmingRemoval(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onRemovalConfirmed}
                disabled={removeSeat.isPending}
              >
                Remove from seat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-4 py-4">
      <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
        {title}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function ReadOnlyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-[color:var(--text-secondary)]">
        {label}
      </span>
      <span className="text-sm text-[color:var(--text-primary)]">
        {value || "—"}
      </span>
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  );
}

function FieldHint({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs text-[color:var(--text-tertiary)]">
      {children}
    </p>
  );
}

/**
 * The Org role label field (E14 UI verification F7).
 *
 * A `<select>` over the SAME `ORGANIZATION_ROLE_LABELS` the Add-person form
 * offers — the constant is imported, never restated. This dialog shipped a free
 * text input, which is the drifted half: Story 14.2's W3 contract settles the
 * CONTROL as "select from a configurable preset list, stored free-form text",
 * and mockups 34 :79-86 and 26 :71-79 carry the same five values twice.
 *
 * FR75 is untouched — the STORAGE stays free-form, and the backend keeps
 * accepting any string. What closes is the open set feeding a derived filter:
 * the roster's role filter builds its options from the DISTINCT labels on the
 * loaded rows (14.11 boundary #6), so "OT Manager" typed by hand beside "OT
 * manager" becomes two filter entries, and a typo becomes a phantom option
 * nobody can clear from the UI.
 *
 * An OFF-LIST current value is prepended as its own option rather than dropped.
 * Rows created before this change (staging almost certainly has some) legitimately
 * carry labels outside the preset, and a select that silently rewrote one to
 * "Clinician" on open would change a person's role as a side effect of looking
 * at them. Never drop a value the user did not change.
 */
function RoleLabelField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const id = "edit-member-org-role-label";
  const hintId = `${id}-hint`;
  const isPreset = (ORGANIZATION_ROLE_LABELS as readonly string[]).includes(value);
  const options = useMemo(() => {
    const base = ORGANIZATION_ROLE_LABELS.map((l) => ({ value: l, label: l }));
    if (isPreset) return base;
    return [{ value, label: value }, ...base];
  }, [isPreset, value]);
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>Org role label</Label>
      <SearchableCombobox
        id={id}
        options={options}
        value={value}
        onChange={onChange}
        aria-describedby={hintId}
        searchAriaLabel="Org role label"
        showSearch={false}
      />
      <FieldHint id={hintId}>{ROLE_LABEL_HINT}</FieldHint>
    </div>
  );
}

/** One of the three FR75 metadata fields the org_admin may edit (14.4 AC8). */
function EditableField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
}) {
  const id = `edit-member-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        maxLength={255}
        aria-describedby={hintId}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </div>
  );
}
